import React, { useCallback, useEffect } from "react";
import {
  GoogleMap,
  useLoadScript,
  DrawingManager,
  HeatmapLayer,
  Marker
} from "@react-google-maps/api";
import { API_KEY } from "../../../api/geofence/google-maps";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  setToPublishFence,
  setBounds,
  setMapCenter,
  setMapZoom,
  setSelectedVertex,
  setSelectedPolygonId,
  fetchOrderLocs
} from "../../../redux/geofence/actions";
import {
  getPolygonOptions,
  mapGoogleMapPolygonToGeoJSONPolygon,
  heatmapGradient
} from "./utils";

import Geofence from "./Geofence";
import CitySelector from "./CitySelector";
import NewCityCta from "./NewCityCta";
import BoundsSearch from "./BoundsSearch";
import ZoomControls from "./ZoomControls";
import GeofenceEditPopup from "./GeofenceEditPopup";
import LowerMenu from "./LowerMenu";
import AssignRMBuddies from "./AssignRMBuddies";
import AssignWarehouses from "./AssignWarehouses";
import "./style.less";
import OrdersDateRange from "./OrdersDateFilter";
import AdditionalCityMultiSelector from "./AdditionalCityMultiSelector";

const mapContainerStyle = { height: "100%", width: "100%" };
const libraries = ["drawing", "visualization"];
export let mapInstance;

const GeofenceDraw = () => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: API_KEY,
    libraries
  });
  const bounds = useSelector(({ geofence }) => geofence.bounds, shallowEqual);
  const center = useSelector(
    ({ geofence }) => geofence.mapCenter,
    shallowEqual
  );
  const selectedVertex = useSelector(({ geofence }) => geofence.selectedVertex);
  const zoom = useSelector(({ geofence }) => geofence.mapZoom);
  const drawingOpts = useSelector(
    ({ geofence }) => geofence.drawingOpts,
    shallowEqual
  );
  const toPublishFence = useSelector(({ geofence }) => geofence.toPublishFence);
  const visibleGeofences = useSelector(
    ({ geofence }) => geofence.visibleGeofences,
    shallowEqual
  );
  const dispatch = useDispatch();
  const visibleLayers = useSelector(
    ({ geofence }) => geofence.visibleLayers,
    shallowEqual
  );
  const currentCityName = useSelector(
    ({ geofence }) => geofence.currentCityName
  );
  const newCityName = useSelector(({ geofence }) => geofence.newCityName);
  const heatmapVisible = useSelector(
    ({ geofence }) => geofence.visibleLayers.indexOf("heatmap") !== -1
  );
  const orderPinsMapVisible = useSelector(
    ({ geofence }) => geofence.visibleLayers.indexOf("orderpins") !== -1
  );
  const orderpins = useSelector(({ geofence }) => geofence.orderpins);
  const viewAssignedWarehousesOf = useSelector(
    ({ geofence }) => geofence.viewAssignedWarehousesOf,
    shallowEqual
  );

  useEffect(() => {
    if (heatmapVisible || orderPinsMapVisible) {
      if (orderpins.length > 0) {
        return;
      }
      if (newCityName && !currentCityName) {
        dispatch(fetchOrderLocs(newCityName));
      } else {
        dispatch(fetchOrderLocs(currentCityName));
      }
    }
  }, [
    currentCityName,
    heatmapVisible,
    dispatch,
    newCityName,
    orderPinsMapVisible,
    orderpins.length
  ]);
  useEffect(() => {
    if (mapInstance && bounds) {
      mapInstance.fitBounds(bounds);

      // Do this bescause of google map fucked up logic of setting bounds
      // In short if using bounds and react state together set bounds once and then use center and zoom in state management.
      // Or in more simpler terms DONT_REMOVE this line
      dispatch(setBounds(null));
    }
  }, [bounds, dispatch]);

  useEffect(() => {
    if (mapInstance) {
      mapInstance.setCenter({
        lat: center.lat,
        lng: center.lng
      });
    }
  }, [center.lat, center.lng]);

  useEffect(() => {
    if (mapInstance) {
      mapInstance.setZoom(zoom);
    }
  }, [zoom]);

  const onIdle = useCallback(() => {
    if (mapInstance.getZoom() && mapInstance.getCenter()) {
      dispatch(setMapZoom(mapInstance.getZoom()));
      dispatch(setMapCenter(mapInstance.getCenter().toJSON()));
    }
  }, [dispatch]);

  let drawingMode = null;
  let fenceCategory = null;
  let editable = null;
  if (drawingOpts) {
    drawingMode = drawingOpts.drawingMode;
    fenceCategory = drawingOpts.fenceCategory;
    editable = drawingOpts.editable;
  }
  const polygonOptions = getPolygonOptions(fenceCategory, editable);
  const polygonDrawnHandler = useCallback(
    polygon => {
      // Hack to properly cancel drawingMode
      // and not draw a polygon when drawingOpts are null
      if (!drawingOpts) {
        polygon.setMap(null);
        return;
      }

      polygon.setOptions({ ...polygon.options, strokeOpacity: 0 });
      dispatch(
        setToPublishFence(
          mapGoogleMapPolygonToGeoJSONPolygon(polygon, {
            fenceCategory: drawingOpts.fenceCategory
          })
        )
      );
      polygon.setMap(null);
    },
    [dispatch, drawingOpts]
  );

  const onFenceUpdate = useCallback(
    updatedFence => {
      dispatch(setToPublishFence(updatedFence));
    },
    [dispatch]
  );

  const onFenceSelect = useCallback(
    _id => {
      if (!toPublishFence) {
        dispatch(setSelectedPolygonId(_id));
      }
    },
    [dispatch, toPublishFence]
  );

  const onVertexSelect = useCallback(
    index => dispatch(setSelectedVertex(index)),
    [dispatch]
  );

  const viewAssignedBuddiesOf = useSelector(
    ({ geofence }) => geofence.viewAssignedBuddiesOf,
    shallowEqual
  );

  // wrapping to a function is useful in case you want to access `window.google`
  const onLoad = React.useCallback(
    function onLoad(mapInstanceObj) {
      // do something with map Instance
      mapInstance = mapInstanceObj;
      if (!bounds) {
        return;
      }
      mapInstance.fitBounds(bounds);
      // Do this bescause of google map fucked up logic of setting bounds
      // In short if using bounds and react state together set bounds once and then use center and zoom in state management.
      // Or in more simpler terms DONT_REMOVE this line
      dispatch(setBounds(null));
    },
    [bounds, dispatch]
  );

  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>;
  }

  return isLoaded ? (
    <GoogleMap
      options={{
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: false
      }}
      onIdle={onIdle}
      mapContainerStyle={mapContainerStyle}
      onLoad={onLoad}
    >
      <CitySelector />
      <AdditionalCityMultiSelector />
      <NewCityCta />
      <BoundsSearch />
      <ZoomControls />
      <GeofenceEditPopup />
      {viewAssignedBuddiesOf && (
        <AssignRMBuddies viewAssignedBuddiesOf={viewAssignedBuddiesOf} />
      )}
      {viewAssignedWarehousesOf && (
        <AssignWarehouses viewAssignedWarehousesOf={viewAssignedWarehousesOf} />
      )}
      <LowerMenu />
      <DrawingManager
        onPolygonComplete={polygonDrawnHandler}
        drawingMode={drawingMode}
        options={{
          drawingControl: false,
          polygonOptions
        }}
      />
      {visibleLayers.indexOf("heatmap") !== -1 ? (
        <div>
          <OrdersDateRange />
          <HeatmapLayer
            data={orderpins.map(e => ({
              location: new window.google.maps.LatLng(
                e.loc.coordinates[1],
                e.loc.coordinates[0]
              ),
              weight: e.count
            }))}
            options={{
              gradient: heatmapGradient,
              dissipating: true,
              maxIntensity: 10,
              radius: 6
            }}
          />
          <img
            className="heatmaplegend"
            alt="Heatmap Legend"
            src={`${process.env.PUBLIC_URL}/heatmaplegend.png`}
          />
        </div>
      ) : null}
      {visibleLayers.indexOf("orderpins") !== -1
        ? orderpins.map((e, index) => (
            <Marker
              key={index}
              position={
                new window.google.maps.LatLng(
                  e.loc.coordinates[1],
                  e.loc.coordinates[0]
                )
              }
              icon={`${process.env.PUBLIC_URL}/order-loc.png`}
            />
          ))
        : null}
      {visibleLayers.indexOf("geofence") !== -1
        ? visibleGeofences.map(e => (
            <Geofence
              onSelectHandler={onFenceSelect}
              key={e._id}
              editable={false}
              fence={e}
            />
          ))
        : null}
      {toPublishFence ? (
        <Geofence
          selectedVertex={selectedVertex}
          onVertexSelect={onVertexSelect}
          onUpdate={onFenceUpdate}
          fence={toPublishFence}
          editable={true}
        />
      ) : null}
    </GoogleMap>
  ) : null; // Add a loader
};

export default GeofenceDraw;
